import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthContextService } from '@core/auth/context/auth-context.service';
import { AuthService } from '@core/auth/internal/auth.service';
import { toSafeInternalUri } from '@core/auth/safe-internal-uri';
import { SessionStore } from '@core/session/session.store';

/** Split path / query / hash so `#fragment` never pollutes query parsing. */
function splitUrlParts(url: string): { path: string; query: string; fragment: string } {
  const hashIndex = url.indexOf('#');
  const withoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? '' : url.slice(hashIndex);

  const queryIndex = withoutHash.indexOf('?');
  if (queryIndex === -1) {
    return { path: withoutHash, query: '', fragment };
  }

  return {
    path: withoutHash.slice(0, queryIndex),
    query: withoutHash.slice(queryIndex + 1),
    fragment,
  };
}

function parseAudienceFromUrl(url: string): string | null {
  const { query } = splitUrlParts(url);
  if (!query) {
    return null;
  }

  const params = new URLSearchParams(query);
  return params.get('aud') === 'internal' ? 'internal' : null;
}

/** Removes `aud` after it has been read — it is an entry hint, not a long-lived claim. */
function stripAudienceFromUrl(url: string): string {
  const { path, query, fragment } = splitUrlParts(url);
  if (!query) {
    return `${path}${fragment}`;
  }

  const params = new URLSearchParams(query);
  params.delete('aud');
  const remainingQuery = params.toString();
  return remainingQuery ? `${path}?${remainingQuery}${fragment}` : `${path}${fragment}`;
}

function createLoginRedirect(router: Router, safeTarget: string): UrlTree {
  const extras = safeTarget !== '/' ? { queryParams: { returnUrl: safeTarget } } : {};
  return router.createUrlTree(['/auth/login'], extras);
}

async function ensureAuthenticated(targetUrl: string): Promise<boolean | UrlTree> {
  const sessionStore = inject(SessionStore);
  const router = inject(Router);
  const authContext = inject(AuthContextService);
  const authService = inject(AuthService);

  const normalizedTargetUrl = targetUrl.trim();
  const audience = parseAudienceFromUrl(normalizedTargetUrl);
  const urlWithoutAudience = stripAudienceFromUrl(normalizedTargetUrl);
  const normalizedTarget = toSafeInternalUri(urlWithoutAudience);
  const hasSafeTarget = normalizedTarget !== null;
  const safeTarget = normalizedTarget ?? '/';

  try {
    if (await sessionStore.isAuthenticated()) {
      return true;
    }

    const storedAuthType = authContext.getStoredAuthType();

    // External clients always use the password/IDX login page — never internal Okta.
    if (storedAuthType === 'external') {
      return createLoginRedirect(router, safeTarget);
    }

    // Latitude / Okta tile append ?aud=internal. Stored internal recovers same-tab sessions.
    const isExplicitInternal =
      (audience === 'internal' && hasSafeTarget) || storedAuthType === 'internal';

    // Unknown / client deep links → client login (no internal Okta).
    if (!isExplicitInternal) {
      return createLoginRedirect(router, safeTarget);
    }

    try {
      await authService.loginWithSSO(undefined, safeTarget);
      return false;
    } catch {
      return createLoginRedirect(router, safeTarget);
    }
  } catch {
    return router.createUrlTree(['/auth/login']);
  }
}

export const oktaCustomGuard: CanActivateFn = async (_route, state) => {
  return ensureAuthenticated(state?.url ?? '/');
};

export const oktaCustomChildGuard: CanActivateChildFn = async (_childRoute, state) => {
  return ensureAuthenticated(state?.url ?? '/');
};
